import type { AxiosError } from 'axios';
import i18n from '@/shared/lib/i18n';

interface ValidationErrorItem {
  msg?: string;
  loc?: (string | number)[];
}

interface ErrorEnvelopeDetail {
  error_code?: string;
  message?: string;
  hint?: string;
}

type ApiDetail = string | ValidationErrorItem[] | ErrorEnvelopeDetail;

interface ApiErrorBody {
  detail?: ApiDetail;
  error_code?: string;
  field_errors?: Record<string, string> | null;
  request_id?: string;
}

function isEnvelopeDetail(detail: ApiDetail | undefined): detail is ErrorEnvelopeDetail {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    !Array.isArray(detail) &&
    ('message' in detail || 'error_code' in detail || 'hint' in detail)
  );
}

function envelopeMessage(detail: ErrorEnvelopeDetail): string {
  // Prefer message; otherwise echo the error_code so the operator at least
  // sees the machine-readable reason.
  return detail.message?.trim() || detail.error_code || '';
}

/**
 * Extracts a human-readable error message from an API error.
 *
 * Handles three response shapes:
 * - String detail: `{ "detail": "message" }` (most errors)
 * - Validation array: `{ "detail": [{ "loc": [...], "msg": "..." }] }` (FastAPI 422)
 * - Envelope object: `{ "detail": { "error_code", "message", "hint" } }` (LOK-1121
 *   envelope used by the venue-connection endpoints and others)
 *
 * Falls back to the provided fallback string when no detail is available.
 */
export function parseApiError(err: unknown, fallback: string): string {
  const detail = (err as AxiosError<ApiErrorBody>)?.response?.data?.detail;

  // Handle string detail (most common — 400, 401, 403, 404, 409)
  if (typeof detail === 'string' && detail.trim()) return detail;

  // Handle ValidationError array (FastAPI 422)
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((e) => {
        const field = e.loc?.filter((l) => l !== 'body').join('.') ?? '';
        const msg = e.msg ?? '';
        return field && msg ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join('; ') : fallback;
  }

  // Handle envelope object (LOK-1121)
  if (isEnvelopeDetail(detail)) {
    const msg = envelopeMessage(detail);
    if (msg) return msg;
  }

  return fallback;
}

/** Structured error with human-readable context for actionable UI feedback. */
export interface ParsedApiError {
  title: string;
  reason?: string;
  action?: string;
  retryable: boolean;
  code?: number;
}

function extractDetailMsg(detail: ApiDetail | undefined): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e) => {
        const field = e.loc?.filter((l) => l !== 'body').join('.') ?? '';
        const msg = e.msg ?? '';
        return field && msg ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join('; ');
  }
  if (isEnvelopeDetail(detail)) return envelopeMessage(detail);
  return '';
}

function extractDetailHint(detail: ApiDetail | undefined): string | undefined {
  if (isEnvelopeDetail(detail) && detail.hint?.trim()) return detail.hint.trim();
  return undefined;
}

/**
 * LOK-125: Returns a structured error with title, reason, action, and retryable flag.
 * Use for toast messages or inline error UI that guides the operator.
 *
 * @param err  - The raw error from an API call
 * @param context - Verb phrase describing the action, e.g. "stop bot", "cancel orders"
 */
export function parseApiErrorStructured(err: unknown, context: string): ParsedApiError {
  const t = i18n.t.bind(i18n);
  const axiosErr = err as AxiosError<ApiErrorBody>;
  const status = axiosErr?.response?.status;
  const detail = axiosErr?.response?.data?.detail;
  const detailMsg = extractDetailMsg(detail);
  const detailHint = extractDetailHint(detail);

  const title = t('common:errors.failedTo', { context });

  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  if (isOffline || !status) {
    return {
      title,
      reason: isOffline ? t('common:errors.noConnection') : t('common:errors.serverUnreachable'),
      action: t('common:errors.checkConnectionRetry'),
      retryable: true,
    };
  }

  if (status === 400) {
    return {
      title,
      reason: detailMsg || t('common:errors.invalidRequest'),
      action: detailHint ?? t('common:errors.checkValuesRetry'),
      retryable: false,
      code: status,
    };
  }
  if (status === 401) {
    return {
      title,
      reason: t('common:errors.sessionExpired'),
      action: t('common:errors.refreshToLogin'),
      retryable: false,
      code: status,
    };
  }
  if (status === 403) {
    return {
      title,
      reason: t('common:errors.noPermission'),
      retryable: false,
      code: status,
    };
  }
  if (status === 404) {
    return {
      title,
      reason: t('common:errors.resourceNotFound'),
      retryable: false,
      code: status,
    };
  }
  if (status === 409) {
    return {
      title,
      reason: detailMsg || t('common:errors.conflictOccurred'),
      action: detailHint ?? (detailMsg ? t('common:errors.resolveConflictRetry') : undefined),
      retryable: true,
      code: status,
    };
  }
  if (status === 422) {
    return {
      title,
      reason: detailMsg || t('common:errors.validationFailed'),
      action: detailHint ?? t('common:errors.checkInputRetry'),
      retryable: false,
      code: status,
    };
  }
  if (status === 429) {
    return {
      title,
      reason: t('common:errors.tooManyRequests'),
      action: t('common:errors.waitAndRetry'),
      retryable: true,
      code: status,
    };
  }
  if (status >= 500) {
    return {
      title,
      reason: t('common:errors.serverError'),
      action: t('common:errors.tryAgainSoon'),
      retryable: true,
      code: status,
    };
  }

  return {
    title,
    reason: detailMsg || t('common:errors.unexpectedError'),
    retryable: true,
    code: status,
  };
}

/**
 * FE-05-05 + LOK-1121: Extract per-field validation errors from a 422 API response.
 * Returns a map of field paths to error messages.
 *
 * Supports two shapes:
 * - FastAPI: `{ detail: [{ loc: ["body", "config", "window_size"], msg: "..." }] }`
 * - Envelope: `{ field_errors: { "config.window_size": "..." } }` at top-level
 *
 * Output: `{ "config.window_size": "..." }`
 */
export function parseFieldErrors(err: unknown): Record<string, string> {
  const axiosErr = err as AxiosError<ApiErrorBody>;
  if (axiosErr?.response?.status !== 422) return {};

  const result: Record<string, string> = {};

  // Envelope shape: top-level field_errors record
  const envelope = axiosErr?.response?.data?.field_errors;
  if (envelope && typeof envelope === 'object') {
    for (const [field, message] of Object.entries(envelope)) {
      if (typeof message === 'string' && message) {
        result[field] = message;
      }
    }
  }

  // FastAPI shape: detail array of validation errors
  const detail = axiosErr?.response?.data?.detail;
  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (!item.loc || !item.msg) continue;
      // Strip "body" prefix that FastAPI always adds
      const path = item.loc.filter((seg) => seg !== 'body').join('.');
      if (path && !(path in result)) {
        result[path] = item.msg;
      }
    }
  }

  return result;
}

/**
 * Apply field-level errors from the API to a React Hook Form instance.
 * Usage: applyFieldErrors(form.setError, parseFieldErrors(err))
 */
export function applyFieldErrors(
  setError: (name: string, error: { type: string; message: string }) => void,
  fieldErrors: Record<string, string>,
): void {
  for (const [field, message] of Object.entries(fieldErrors)) {
    setError(field, { type: 'server', message });
  }
}
